import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { Users, Plus, Search, Trash2, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { KPI } from '@/components/ui/kpi';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/utils';
import {
  useWorkspaceUsers,
  useUpdateWorkspaceUser,
  useDeleteWorkspaceUser,
  type WorkspaceUser,
  type WorkspaceRole,
} from '@/lib/hooks/workspace-users';
import { UserInviteDrawer } from '@/features/usuarios/UserInviteDrawer';

export const Route = createFileRoute('/_app/usuarios')({
  component: UsuariosPage,
});

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  WORKSPACE_ADMIN: 'admin',
  OPERADOR: 'operador',
  CONTADOR_EXTERNO: 'contador',
};

type RoleTone = 'mint' | 'lilac' | 'default';

const ROLE_TONE: Record<WorkspaceRole, RoleTone> = {
  WORKSPACE_ADMIN: 'mint',
  OPERADOR: 'lilac',
  CONTADOR_EXTERNO: 'default',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

function UsuariosPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceUser | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useWorkspaceUsers(workspaceId);
  const updateMut = useUpdateWorkspaceUser(workspaceId);
  const deleteMut = useDeleteWorkspaceUser(workspaceId);

  const users = data?.items ?? [];

  const filtered = search.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      )
    : users;

  async function handleRoleChange(u: WorkspaceUser, newRole: WorkspaceRole) {
    try {
      await updateMut.mutateAsync({ id: u.id, role: newRole });
      toast.success(`Perfil de ${u.name} atualizado.`);
    } catch {
      toast.error('Falha ao atualizar o perfil. Tente novamente.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success(`${deleteTarget.name} foi removido do workspace.`);
      setDeleteTarget(null);
    } catch {
      toast.error('Falha ao remover usuário. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  }

  if (!workspaceId) {
    return (
      <div className="flex flex-col gap-5">
        <header>
          <Eyebrow>Membros</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Usuários do workspace
          </h1>
        </header>
        <Card variant="default" className="p-10 text-center">
          <p className="text-sm text-fg-muted">
            Você precisa estar dentro de um workspace para gerenciar usuários.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Membros</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Usuários do workspace
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            Gerencie quem tem acesso e qual o perfil de cada membro.
          </p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Convidar usuário
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          label="Total de membros"
          value={isLoading ? '…' : String(users.length)}
          icon={Users}
          tone="mint"
        />
        <KPI
          label="Administradores"
          value={isLoading ? '…' : String(users.filter((u) => u.role === 'WORKSPACE_ADMIN').length)}
          icon={Users}
          tone="default"
        />
        <KPI
          label="Operadores"
          value={isLoading ? '…' : String(users.filter((u) => u.role === 'OPERADOR').length)}
          icon={Users}
          tone="default"
        />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted pointer-events-none" strokeWidth={1.6} />
        <Input
          className="pl-9"
          placeholder="Buscar por nome ou e-mail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Buscar usuários"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-card-soft rounded-3 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card variant="default" className="p-10 text-center">
          <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-mint-soft text-mint-deep shadow-[inset_0_2px_4px_rgba(40,90,70,0.10)]">
              <Users className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <div>
              {search ? (
                <>
                  <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg">
                    Nenhum resultado
                  </h3>
                  <p className="text-sm text-fg-muted mt-1">
                    Nenhum usuário corresponde a "{search}". Tente outro termo.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg">
                    Nenhum usuário ainda
                  </h3>
                  <p className="text-sm text-fg-muted mt-1">
                    Convide alguém para colaborar neste workspace.
                  </p>
                </>
              )}
            </div>
            {!search && (
              <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={1.8} />
                Convidar primeiro usuário
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-3 border border-card-stroke overflow-hidden">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-card-stroke bg-card-soft">
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted px-4 py-3 w-[280px]">
                    Nome
                  </th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted px-4 py-3">
                    E-mail
                  </th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted px-4 py-3 w-[180px]">
                    Perfil
                  </th>
                  <th className="text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted px-4 py-3 w-[130px]">
                    Convidado em
                  </th>
                  <th className="px-4 py-3 w-[48px]" aria-label="Ações" />
                </tr>
              </thead>
              <tbody className="divide-y divide-card-stroke bg-card">
                {filtered.map((u) => {
                  const isSelf = u.id === user?.id;
                  const isUpdating = updateMut.isPending && updateMut.variables?.id === u.id;
                  return (
                    <tr key={u.id} className="group hover:bg-card-soft/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="grid h-9 w-9 place-items-center rounded-full bg-mint-soft text-mint-deep text-[13px] font-bold font-mono shrink-0 select-none">
                            {getInitials(u.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-fg truncate">{u.name}</p>
                            {isSelf && (
                              <span className="font-mono text-[10px] text-fg-muted">você</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-fg-muted truncate max-w-[200px]">
                        {u.email}
                      </td>
                      <td className="px-4 py-3">
                        {isSelf ? (
                          <Pill tone={ROLE_TONE[u.role]} dot>
                            {ROLE_LABELS[u.role]}
                          </Pill>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Select
                              value={u.role}
                              onValueChange={(v) => handleRoleChange(u, v as WorkspaceRole)}
                              disabled={isUpdating}
                            >
                              <SelectTrigger className="h-8 text-xs w-[160px]" aria-label={`Perfil de ${u.name}`}>
                                {isUpdating ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.8} />
                                ) : (
                                  <SelectValue />
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="WORKSPACE_ADMIN">Administrador</SelectItem>
                                <SelectItem value="OPERADOR">Operador</SelectItem>
                                <SelectItem value="CONTADOR_EXTERNO">Contador externo</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-fg-muted tabular-nums">
                        {fmtDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        {!isSelf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-block hover:text-block hover:bg-block-soft"
                            onClick={() => setDeleteTarget(u)}
                            aria-label={`Remover ${u.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((u) => {
              const isSelf = u.id === user?.id;
              const isUpdating = updateMut.isPending && updateMut.variables?.id === u.id;
              return (
                <Card key={u.id} variant="default" className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-mint-soft text-mint-deep text-[13px] font-bold font-mono shrink-0 select-none">
                      {getInitials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-fg">{u.name}</p>
                        {isSelf && (
                          <span className="font-mono text-[10px] text-fg-muted">você</span>
                        )}
                        <Pill tone={ROLE_TONE[u.role]} dot>
                          {ROLE_LABELS[u.role]}
                        </Pill>
                      </div>
                      <p className="font-mono text-xs text-fg-muted mt-0.5 truncate">{u.email}</p>
                      <p className="text-xs text-fg-dim mt-1">Convidado em {fmtDate(u.createdAt)}</p>
                    </div>
                  </div>
                  {!isSelf && (
                    <div className="mt-3 pt-3 border-t border-card-stroke flex items-center justify-between gap-3">
                      <Select
                        value={u.role}
                        onValueChange={(v) => handleRoleChange(u, v as WorkspaceRole)}
                        disabled={isUpdating}
                      >
                        <SelectTrigger className="h-8 text-xs flex-1" aria-label={`Perfil de ${u.name}`}>
                          {isUpdating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.8} />
                          ) : (
                            <SelectValue />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WORKSPACE_ADMIN">Administrador</SelectItem>
                          <SelectItem value="OPERADOR">Operador</SelectItem>
                          <SelectItem value="CONTADOR_EXTERNO">Contador externo</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-block hover:text-block hover:bg-block-soft shrink-0"
                        onClick={() => setDeleteTarget(u)}
                        aria-label={`Remover ${u.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <UserInviteDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        workspaceId={workspaceId}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover usuário"
        description={
          deleteTarget
            ? `Tem certeza que deseja remover ${deleteTarget.name} do workspace? O acesso será revogado imediatamente.`
            : undefined
        }
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        tone="danger"
        loading={!!deletingId}
        onConfirm={handleDelete}
      />
    </div>
  );
}
